import React from 'react';
import {
  Box,
  Pagination as MuiPagination,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

export interface PaginationMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

interface PaginationProps {
  meta: PaginationMeta;
  onPageChange: (page: number) => void;
  onLimitChange: (limit: number) => void;
  limitOptions?: number[];
}

const Pagination: React.FC<PaginationProps> = ({
  meta,
  onPageChange,
  onLimitChange,
  limitOptions = [5, 10, 20, 50],
}) => {
  const { t } = useTranslation();
  const { page, limit, total, totalPages } = meta;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // Ensure page is always a number
  const currentPage = Number(page);
  const currentLimit = Number(limit);
  const currentTotal = Number(total);
  const currentTotalPages = Number(totalPages);

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    onPageChange(value);
  };

  const handleLimitChange = (event: any) => {
    onLimitChange(event.target.value);
  };

  const startItem = (currentPage - 1) * currentLimit + 1;
  const endItem = Math.min(currentPage * currentLimit, currentTotal);

  return (
    <Box sx={{ width: '100%', mt: 3 }}>
      {/* Top row with centered pagination controls */}
      {currentTotalPages > 1 && (
        <Box display="flex" justifyContent="center" sx={{ mb: 1, width: '100%' }}>
          <MuiPagination
            count={currentTotalPages}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
            showFirstButton={!isMobile}
            showLastButton={!isMobile}
            siblingCount={isMobile ? 0 : 1}
            boundaryCount={isMobile ? 1 : 1}
            size={isMobile ? 'small' : 'medium'}
          />
        </Box>
      )}

      {/* Bottom row with results info and per page selector */}
      <Box 
        display="flex" 
        justifyContent={isMobile ? "center" : "flex-end"} 
        alignItems="center" 
        gap={isMobile ? 1 : 1} 
        sx={{ 
          width: '100%',
          flexDirection: isMobile ? 'column' : 'row',
          textAlign: isMobile ? 'center' : 'right'
        }}
      >
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}>
          {t('pagination.showing')} {startItem} {t('pagination.to')} {endItem} {t('pagination.of')} {currentTotal} {t('pagination.results')}
        </Typography>
        <FormControl size="small" sx={{ minWidth: isMobile ? 70 : 80 }}>
          <InputLabel sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}>{t('pagination.itemsPerPage')}</InputLabel>
          <Select
            value={currentLimit}
            onChange={handleLimitChange}
            label={t('pagination.itemsPerPage')}
            sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}
          >
            {limitOptions.map((option) => (
              <MenuItem key={option} value={option} sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    </Box>
  );
};

export default Pagination;
